import { FC, useRef } from 'react';
import { useInstance } from '@meraki-internal/react-dependency-injection';
import { CareMapState } from '../caremap/CareMapState';
import { useSubscription } from '@meraki-internal/state';
import { createUseStyles } from 'react-jss';
import { CommunityModelFactory } from './CommunityModelFactory';
import { UserState } from '../user/UserState';
import dayjs from 'dayjs';
import { Spinner } from '../components/Spinner';
import { RevenueCatModel } from '../innerhive-plus/revenue-cat/RevenueCatModel';
import { CollaboratorRowAdapter } from './CollaboratorRowAdapter';
import { ICaremapAccess } from '../caremap/ICareMap';

const useStyles = createUseStyles({
    collaboratorList: {
        paddingTop: 12,
        paddingBottom: 12,
        overflowY: 'auto',
        overflowX: 'hidden',
        display: 'flex',
        flexDirection: 'column',
        gap: 12,
    },

    spinnerWrapper: {
        // must match height of collaboratorList below
        minHeight: 235,

        textAlign: 'center',
        display: 'flex',
        flexDirection: 'column',
        justifyContent: 'space-around'
    },
});

type ICareMapCollaboratorManager = {
    section: 'viewers' | 'co-owners';
};

export const CareMapCollaboratorList: FC<ICareMapCollaboratorManager> = ({ section }) => {
    const classes = useStyles();

    const listRef = useRef<HTMLDivElement>(null);

    const userState = useInstance(UserState);
    useSubscription(() => userState);

    const careMapState = useInstance(CareMapState);
    useSubscription(() => careMapState);

    const factory = useInstance(CommunityModelFactory);
    useSubscription(() => factory);
    const community = factory.forActiveCaremap();

    const revCat = useInstance(RevenueCatModel);
    useSubscription(() => revCat);

    const owner = community.getOwner();
    const loaded = community.isLoaded();

    const filterToAccess: ICaremapAccess = section === 'co-owners' ? 'co-owner' : 'view-only';
    const sortedInvites = community.getInvites(filterToAccess)
        .sort((n1, n2) => dayjs(n1.createdAt).isAfter(n2.createdAt) ? -1 : 1);

    if (!loaded){
        return (
            <div
                className={classes.spinnerWrapper}
                data-type="collaborator-list"
                data-list-type={section}
                data-loaded={false}
            >
                <Spinner />
            </div>
        );
    }

    return (
        <div
            ref={listRef}
            className={classes.collaboratorList}
            data-type="collaborator-list"
            data-list-type={section}
            data-loaded={true}
            style={revCat.hasInnerhivePlus() ? undefined : {
                marginTop: 24,
            }}
        >
            {section === 'co-owners' && owner && (
                <>
                    <CollaboratorRowAdapter
                        owner={owner}
                    />
                    {community.getPermanentCoOwners().map(collaborator => (
                        <CollaboratorRowAdapter
                            key={collaborator.userId}
                            permission={collaborator}
                        />
                    ))}
                </>
            )}

            {section === 'viewers' && (
                community.getPermanentViewers().map(collaborator => (
                    <CollaboratorRowAdapter
                        key={collaborator.userId}
                        permission={collaborator}
                    />
                ))
            )}

            {sortedInvites.map(invite => (
                <CollaboratorRowAdapter
                    key={invite.token}
                    invite={invite}
                />
            ))}
        </div>
    );
};
